import { Alert, Box, LinearProgress, Typography } from "@mui/material";
import type { FC } from "react";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import AcquiredCustomersTable from "./components/AcquiredCustomersTable";
import ComparisonPanes from "./components/ComparisonPanes";
import KpiSections from "./components/KpiSections";
import ReportFilterPanel from "./components/ReportFilterPanel";
import ReportHeader from "./components/ReportHeader";
import ReportSubsection from "./components/ReportSubsection";
import SellerMultiSelect from "./components/SellerMultiSelect";
import { activitySections } from "./kpiSections";
import { REPORTS } from "./reportCatalog";
import { formatActiveFilters } from "./statisticsFormat";
import { useAppliedFilters } from "./useAppliedFilters";
import { useSellerActivityStatistics } from "./useSellerActivityStatistics";
import { useSellerNames } from "./useSellers";

interface PaneFilters {
  readonly sellerIds: string[];
  readonly from: string;
  readonly to: string;
}

function defaultPaneAFilters(): PaneFilters {
  return { sellerIds: [], from: dayjs().startOf("month").format(ISO_DATE_FORMAT), to: dayjs().format(ISO_DATE_FORMAT) };
}

function defaultPaneBFilters(): PaneFilters {
  return {
    sellerIds: [],
    from: dayjs().subtract(1, "month").startOf("month").format(ISO_DATE_FORMAT),
    to: dayjs().subtract(1, "month").endOf("month").format(ISO_DATE_FORMAT),
  };
}

interface PaneProps {
  readonly initialFilters: PaneFilters;
}

const ReportPane: FC<PaneProps> = ({ initialFilters }) => {
  const { draft, applied, isDirty, setDraft, apply } = useAppliedFilters<PaneFilters>(initialFilters);
  const { data, isLoading, error } = useSellerActivityStatistics(applied.sellerIds, {
    from: applied.from,
    to: applied.to,
  });
  const sellerNames = useSellerNames(applied.sellerIds);

  return (
    <Box>
      <ReportFilterPanel
        subject={
          <SellerMultiSelect sellerIds={draft.sellerIds} onChange={(sellerIds) => setDraft({ ...draft, sellerIds })} />
        }
        dateLabel="Data aktywności"
        range={draft}
        onRangeChange={({ from, to }) => setDraft({ ...draft, from, to })}
        onApply={apply}
        applyDisabled={!isDirty}
        activeFilters={formatActiveFilters(sellerNames, applied.from, applied.to)}
      />
      {applied.sellerIds.length === 0 && (
        <Typography sx={{ color: "text.secondary" }}>Wybierz sprzedawców, aby zobaczyć statystyki</Typography>
      )}
      {isLoading && <LinearProgress data-testid="loading-indicator" />}
      {error && <Alert severity="error">Nie udało się pobrać statystyk.</Alert>}
      {data && (
        <>
          <KpiSections sections={activitySections(data)} />
          <ReportSubsection
            title="Pozyskanie klientów (Active Lead / Lead)"
            description={`Klienci, którzy w okresie ${applied.from} — ${applied.to} zmienili status na Active Lead lub Lead. Sprzedawcy: ${sellerNames}.`}
          >
            <AcquiredCustomersTable rows={data.acquiredCustomers} />
          </ReportSubsection>
        </>
      )}
    </Box>
  );
};

const SellerActivityReportPage: FC = () => (
  <Box>
    <ReportHeader report={REPORTS["sellers-activity"]} />
    <ComparisonPanes
      leftLabel="Porównanie A"
      rightLabel="Porównanie B"
      left={<ReportPane initialFilters={defaultPaneAFilters()} />}
      right={<ReportPane initialFilters={defaultPaneBFilters()} />}
    />
  </Box>
);

export default SellerActivityReportPage;
